<template>
  <b-modal
    :visible="value"
    @hide="handleHide"
    hide-footer
    centered
    size="lg"
    :title="modalTitle"
    body-class="p-3"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
  >
    <!-- Stars -->
    <div class="d-flex align-items-center mb-2">
      <div class="mr-2 font-weight-bold">{{ $t('survey.stars') }}</div>
      <div class="survey-stars">
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          class="survey-star btn p-0"
          :class="{ 'is-active': n <= rating }"
          @click="setRating(n)"
        >
          ★
        </button>
      </div>
      <div class="ml-3 text-muted" v-if="rating">
        {{ ratingLabel }}
      </div>
    </div>
    <div class="text-muted small mb-3">
      {{ $t('survey.title.optional') }}
    </div>
    <!-- Message for <=4 -->
    <b-alert v-if="rating > 0 && rating <= 4" show variant="secondary" class="small">
      {{  $t('survey.text.thankyou') }}
    </b-alert>
    <!-- Reason required for <=4 -->
    <b-form-group
      v-if="rating > 0 && rating <= 4"
      :label="$t('survey.label.telluswhy')"
      label-for="survey-reason"
      class="mb-3"
    >
      <b-form-select
        id="survey-reason"
        v-model="reasonKey"
        :options="reasonOptions"
        :state="reasonState"
      />
      <b-form-invalid-feedback :state="reasonState">
        {{ $t('survey.label.feedbackreason') }}
      </b-form-invalid-feedback>
    </b-form-group>
    <!-- Comments for any rating -->
    <b-form-group v-if="rating > 0" :label="$t('survey.label.comments')" label-for="survey-comments">
      <b-form-textarea
        id="survey-comments"
        v-model="comments"
        :maxlength="1000"
        rows="5"
        :placeholder="$t('survey.placeholder.comments')"
      />
      <div class="d-flex justify-content-end text-muted small mt-1">
        {{ comments.length }} / 1000
      </div>
    </b-form-group>
    <!-- Phone, Name, and Email fields -->
    <div v-if="rating > 0">
      <b-form-group :label="$t('survey.placeholder.emailAddress')" class="mb-3">
        <b-form-input
          v-model.trim="emailAddress"
          type="email"
          maxlength="100"
          :placeholder="$t('survey.placeholder.emailAddress')"
          @blur="emailBlurred = true"
        />
        <b-form-invalid-feedback :state="emailState">
          {{ $t('survey.error.invalidEmail') }}
        </b-form-invalid-feedback>

      </b-form-group>
      <b-form-group :label="$t('survey.label.phonenumber')" class="mb-3">
        <div class="d-flex align-items-center">
          <b-form-input
            v-model.trim="phoneNumber"
            type="tel"
            maxlength="25"
            :placeholder="$t('survey.placeholder.phonenumber')"
          />
          <b-button
            variant="link"
            class="text-muted ml-2 p-0"
            @click="clearPhone"
            aria-label="Clear phone"
            title="Clear"
          >
            ✕
          </b-button>
        </div>
        <div class="text-muted small mt-1">{{ $t('survey.placeholder.phonenumber') }}</div>
      </b-form-group>
      <b-form-group :label="$t('survey.placeholder.name')" class="mb-3">
        <b-form-input
          v-model.trim="customerName"
          type="text"
          maxlength="100"
          :placeholder="$t('survey.placeholder.name')"
        />
      </b-form-group>
    </div>
    <b-alert v-if="submitError" show variant="danger" class="small mt-2">
      {{ submitError }}
    </b-alert>
    <div class="d-flex justify-content-between mt-3">
      <b-button variant="outline-secondary" @click="skip" :disabled="submitting">
        {{ $t('survey.button.skip') }}
      </b-button>
      <b-button
        variant="primary"
        @click="submit"
        :disabled="submitting || rating === 0"
      >
        <span v-if="submitting">
          <b-spinner small class="mr-2" /> {{ $t('survey.spinner.submitting') }}
        </span>
        <span v-else>{{ $t('survey.button.submit') }}</span>
      </b-button>
    </div>
  </b-modal>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
export default {
  name: 'ClientSurveyModal',
  props: {
    value: { type: Boolean, default: false }, // v-model
    workOrderId: { type: Number, required: true }
  },
  data() {
    return {
      rating: 0,
      reasonKey: null,
      comments: '',
      triedSubmit: false,
      submitting: false,
      submitError: '',
      emailAddress: '',
      emailBlurred: false,
      phoneNumber: '',
      customerName: ''
    }
  },
  computed: {
    ...mapGetters('auth', ['userProfile']),
    modalTitle() {
      return this.$t('survey.title')
    },
    surveyUserName() {
      return this.userProfile ? this.userProfile.firstName + ' ' + this.userProfile.lastName : ''
    },
    surveyUserPhone() {
      return this.userProfile ? this.userProfile.phone : ''
    },
    surveyUserEmail() {
      return this.userProfile ? this.userProfile.email : ''
    },
    emailState() {
      if (!this.emailAddress) return null
      if (!this.triedSubmit && !this.emailBlurred) return null
      const emailRegex = /^[^\s@.][^\s@]*@[^\s@.]+(\.[^\s@.]+)+$/
      return emailRegex.test(this.emailAddress)
    },
    ratingLabel() {
      switch (this.rating) {
        case 1: return this.$t('survey.rating.inadequate')
        case 2: return this.$t('survey.rating.poor')
        case 3: return this.$t('survey.rating.fair')
        case 4: return this.$t('survey.rating.good')
        case 5: return this.$t('survey.rating.excellent')
        default: return ''
      }
    },
    reasonOptions() {
      return [
        { value: null, text: this.$t('survey.prompt.selectreason') },
        { value: 'tech_response_time', text: this.$t('survey.option.reason.responsetime') },
        { value: 'connectivity', text: this.$t('survey.option.reason.connectivity') },
        { value: 'could_not_complete', text: this.$t('survey.option.reason.notcomplete') },
        { value: 'technician_communication', text: this.$t('survey.option.reason.communication') },
        { value: 'usability', text: this.$t('survey.option.reason.usability') },
        { value: 'other', text: this.$t('survey.option.reason.other') }
      ]
    },
    reasonState() {
      if (!(this.rating > 0 && this.rating <= 4)) return null
      if (!this.triedSubmit) return null
      return !!this.reasonKey
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.submitError = ''
        this.triedSubmit = false
        this.emailBlurred = false
        this.prefillContactFields()
      }
    },
    rating(n) {
      if (n === 5) this.reasonKey = null
      this.triedSubmit = false
      this.submitError = ''
    },
    surveyUserName() {
      this.prefillContactFields()
    },
    surveyUserPhone() {
      this.prefillContactFields()
    },
    surveyUserEmail() {
      this.prefillContactFields()
    },
    emailAddress(newVal) {
      if (!newVal) {
        this.emailBlurred = false
      }
    }
  },
  methods: {
    ...mapActions('clientSurvey', ['upsert']),
    prefillContactFields() {
      if (!this.value) return
      if (!this.emailAddress && this.surveyUserEmail) {
        this.emailAddress = this.surveyUserEmail
      }
      if (!this.customerName && this.surveyUserName) {
        this.customerName = this.surveyUserName
      }
      if (!this.phoneNumber && this.surveyUserPhone) {
        this.phoneNumber = this.surveyUserPhone
      }
    },
    setRating(n) {
      this.rating = n
    },
    handleHide() {
      this.$emit('input', false)
    },
    clearPhone() {
      this.phoneNumber = ''
    },
    skip() {
      this.$emit('skipped', { workOrderId: this.workOrderId })
      this.$emit('input', false)
    },
    validate() {
      this.triedSubmit = true
      if (this.rating === 0) return false
      if (this.rating <= 4 && !this.reasonKey) return false
      if (this.emailAddress) {
        const emailRegex = /^[^\s@.][^\s@]*@[^\s@.]+(\.[^\s@.]+)+$/
        if (!emailRegex.test(this.emailAddress)) return false
      }
      return true
    },
    async submit() {
      this.submitError = ''
      if (!this.validate()) return
      this.submitting = true
      try {
        const payload = {
          workOrderId: this.workOrderId,
          feedbackRating: this.rating,
          feedbackReasonKey: this.rating <= 4 ? this.reasonKey : null,
          feedbackComments: this.comments || null,
          feedbackEmailAddress: this.emailAddress || null,
          feedbackPhoneNumber: this.phoneNumber || null,
          feedbackCustomerName: this.customerName || null
        }
        const res = await this.upsert(payload)
        const requestFeedbackId = res && (res.requestFeedbackId || res.RequestFeedbackId)
        this.$emit('submitted', {
          workOrderId: this.workOrderId,
          requestFeedbackId: requestFeedbackId || null,
          rating: this.rating,
          reasonKey: this.reasonKey,
          comments: this.comments,
          emailAddress: this.emailAddress,
          phoneNumber: this.phoneNumber,
          customerName: this.customerName
        })
        this.$emit('input', false)
      } catch (e) {
        this.submitError = (e && e.message) ? e.message : this.$t('survey.error.submissionfailed')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
<style scoped>
.survey-stars { display: inline-flex; line-height: 1; }
.survey-star {
  font-size: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #cfd4da;
  margin-right: 6px;
}
.survey-star.is-active { color: #007bff; }
.survey-star:focus { outline: none; }
</style>



// WEBPACK FOOTER //
// src/components/survey/ClientSurveyModal.vue